import { Text } from '@southfields-digital/mpxlive-components';
import { useMemo, useRef, useState } from 'react';

import useOutsideClick from 'src/hooks/useOutsideClick';

import styles from '../MatchControl.module.scss';
import type { Precision } from '../types';
import { getFormattedTime } from '../utils';

import MatchControlIcon from './MatchControlIcon';

type TimeInProps = {
  time: number;
  extraTime?: number;
  precision?: Precision;
  handleChangeTime?: (time: number) => void;
};

const TimeIn = ({ time, extraTime, precision = 'minutes', handleChangeTime }: TimeInProps) => {
  const [editing, setEditing] = useState<boolean>(false);
  const [minutesInput, setMinutesInput] = useState<number | undefined>(0);
  const [secondsInput, setSecondsInput] = useState<number | undefined>(0);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const formatted = useMemo(() => getFormattedTime(time, precision), [time, precision]);

  const handleToggleEditingTime = () => {
    if (minutesInput == null || secondsInput == null) return;

    if (!editing) {
      setMinutesInput(Math.floor(time / 60000));
      setSecondsInput(Math.floor((time % 60000) / 1000));
    } else {
      const timeDifference = minutesInput * 60000 + secondsInput * 1000 - time;
      handleChangeTime?.(timeDifference);
    }
    setEditing(!editing);
  };

  useOutsideClick(containerRef, () => {
    if (editing) {
      handleToggleEditingTime();
    }
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setInput: React.Dispatch<React.SetStateAction<number | undefined>>
  ) => {
    const { value } = e.currentTarget;

    if (!value.length) {
      setInput(undefined);
      return;
    }

    setInput(+value);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Enter':
        handleToggleEditingTime();
        break;
      case 'Escape':
        setEditing(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className={styles.TimeInWrapper}>
      <div className={styles.TimeInHeaderWrapper}>
        {editing ? (
          <MatchControlIcon
            iconProps={{ icon: 'x', color: '#5B5D70' }}
            onClick={handleToggleEditingTime}
          />
        ) : (
          <MatchControlIcon
            iconProps={{ icon: 'pencil', color: '#5B5D70', weight: 'fill' }}
            onClick={handleToggleEditingTime}
          />
        )}

        {editing ? (
          <div ref={containerRef} className="flex w-[90px] mx-auto">
            <input
              type="number"
              value={minutesInput}
              min={0}
              onChange={(e) => handleInputChange(e, setMinutesInput)}
              onKeyDown={handleInputKeyDown}
              className="flex-1 w-[45px] bg-transparent text-white font-bold text-xl"
            />
            <input
              type="number"
              value={secondsInput}
              maxLength={2}
              onChange={(e) => handleInputChange(e, setSecondsInput)}
              onKeyDown={handleInputKeyDown}
              min={0}
              className="flex-1 w-[45px] bg-transparent text-white font-bold text-xl"
            />
          </div>
        ) : (
          <Text as="h2" color="light" className="mx-auto">
            {formatted}
          </Text>
        )}

        <div className="w-[1px] h-5 bg-gray-700" />
        <Text as="h2" color="light" className="mx-auto">
          + {extraTime ? extraTime / 1000 : 0}
        </Text>
      </div>
    </div>
  );
};

export default TimeIn;
